import { useState, useEffect, useRef } from 'react';
import MicButton from './ui/MicButton';
import Button from './ui/Button';

/**
 * FeedbackInterview — renders the CURRENT adaptive clarification question.
 *
 * The server is turn-based and returns one question per turn. This component shows it
 * and reports the answer via onAnswer(questionText, answer); the parent POSTs it and
 * either swaps `questions` with the server's next turn or finalizes when needs_more is
 * false — so the SERVER, not local index math, decides when the interview ends. Each
 * answer is posted immediately, so the parent's "Skip remaining questions" never strands
 * a locally-held answer. The parent owns that explicit skip/complete action.
 */
export default function FeedbackInterview({ questions = [], onAnswer, loading = false }) {
  const [freeText, setFreeText] = useState('');
  // Guards a rapid double-click on a question from firing onAnswer twice (double-POST)
  // before React re-renders into the loading state. Reset when the next turn arrives.
  const answered = useRef(false);

  const question = questions[0] || null;

  // Clear the input when a new turn arrives.
  useEffect(() => { setFreeText(''); }, [questions]);
  // Re-enable input once a turn settles — a new question on success, or the SAME
  // question on error — so the user can answer/retry. The guard still blocks a
  // double-click while the POST is in flight (loading === true).
  useEffect(() => { if (!loading) answered.current = false; }, [loading]);

  function submitAnswer(answer) {
    if (!question || answered.current) return;
    answered.current = true;
    onAnswer(question.text, answer);
  }

  function handleFreeSubmit() {
    const val = freeText.trim();
    if (val) submitAnswer(val);
  }

  if (loading) {
    return (
      <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400 animate-pulse">
        Thinking…
      </div>
    );
  }

  if (!question) return null;

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{question.text}</p>

      {(question.type === 'yes_no' || question.type === 'single_select') && (
        <div className="flex flex-wrap gap-2">
          {(question.options || []).map((opt) => (
            <button
              key={opt.id}
              onClick={() => submitAnswer(opt.label)}
              className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-slate-600 text-sm hover:bg-primary hover:text-white hover:border-primary transition-colors"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {(question.type === 'free_text' || question.allow_free_text) && (
        <div className="flex gap-2 items-end">
          <textarea
            rows={2}
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleFreeSubmit(); } }}
            placeholder={question.type === 'free_text' ? 'Type your answer…' : 'Or type a custom answer…'}
            className="flex-1 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <div className="flex flex-col gap-1">
            <MicButton onTranscript={(t) => setFreeText((prev) => (prev ? prev + ' ' + t : t).trim())} size={14} />
            <Button size="sm" onClick={handleFreeSubmit} disabled={!freeText.trim()}>Send</Button>
          </div>
        </div>
      )}
    </div>
  );
}
